// foodChatsMock.js (simulador · comida para llevar, 28/9/2026) — la sección «Chats» de la demo con
// conversaciones que parecen pedidos REALES hechos con el producto: el cliente escribe o pulsa el
// enlace, recibe el Formulario (Flow) de pedido, lo completa (su resumen llega como respuesta del
// Flow) y le llega la confirmación con el TICKET y los botones; después, los avisos reales
// (pago recibido, «listo para recoger»), el cliente que da las gracias, el que cancela con el botón
// y el que repite con «Nuevo pedido».
//
// TODO CUADRA CON EL TABLERO: cada chat sale de un pedido de ./seedPedidos.js (mismo cliente,
// teléfono, platos, importe, hora y estado; las marcas de tiempo, de su created_at/paid_at/
// status_history). Los textos que recibe el cliente son los de producción (./customerMessages.js).
// Los mensajes van en español literal en todos los idiomas: es lo que el cliente ve en WhatsApp.

import { INITIAL_PEDIDOS } from "./seedPedidos";
import {
  BTN_CANCEL_ORDER,
  BTN_NEW_ORDER,
  BTN_PAY_ONLINE,
  FLOW_INVITE_BODY,
  FLOW_INVITE_FOOTER,
  FLOW_INVITE_CTA,
  flowInviteHeader,
  TPL_READY_CONEXT,
  CANCEL_REPLIES,
  fillTemplate,
  flowConfirmText,
  templateConfirmText,
  paidNoticeText,
} from "./customerMessages";

export const FOOD_CHAT_BUSINESS = "La Cazuela de María";

// Precio unitario de cada plato de la demo (la carta del Flow simulado + los guisos del menú).
// Un test comprueba que, con estos precios, cada pedido del chat suma EXACTAMENTE el total del
// tablero (envío incluido).
export const DEMO_PRICES = {
  "Paella valenciana": 12.9,
  "Coca-Cola 33cl": 2.5,
  "Albóndigas en salsa": 8.9,
  "Tortilla de patatas": 6.9,
  "Lentejas con chorizo": 7.5,
  "Agua mineral 50cl": 1.8,
  "Merluza al horno": 12.5,
  "Flan de huevo casero": 3.5,
  "Croquetas de jamón ibérico": 6.5,
  "Cocido madrileño": 11.5,
  "Pollo al ajillo": 9.5,
  "Ensaladilla rusa": 5.9,
};

const MIN = 60_000;
const at = (iso, minutes = 0) => new Date(new Date(iso).getTime() + minutes * MIN).toISOString();
/** Hace `days` días a las HH:MM (hora local del navegador; solo para pedidos antiguos). */
const daysAgoAt = (days, hh, mm) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  d.setHours(hh, mm, 0, 0);
  return d.toISOString();
};
const comma = (n) => Number(n).toFixed(2).replace(".", ",");
const firstName = (name) => String(name || "").trim().split(/\s+/)[0] || "Cliente";
const order = (id) => INITIAL_PEDIDOS.find((p) => p.order_id === id);

/** Líneas con precio unitario a partir de los items del pedido del tablero. */
export function orderLines(items) {
  return (items || []).map((it) => ({ qty: Number(it.qty) || 1, name: it.name, price: DEMO_PRICES[it.name] ?? 0 }));
}
export const linesSubtotal = (lines) => Math.round(lines.reduce((s, l) => s + l.price * l.qty, 0) * 100) / 100;

/**
 * Pedido «de chat» a partir de un pedido del tablero (o de uno antiguo que solo vive en el chat).
 * { name, lines, time, delivery: null | { address, feeCents }, total }
 */
function chatOrder({ name, items, time, address = null, feeCents = 0 }) {
  const lines = orderLines(items);
  const delivery = address ? { address, feeCents } : null;
  const total = Math.round((linesSubtotal(lines) + (delivery ? feeCents / 100 : 0)) * 100) / 100;
  return { name, lines, time, delivery, total };
}
const fromSeed = (p) =>
  chatOrder({
    name: p.contact_name,
    items: p.items,
    time: p.pickup_time,
    address: p.fulfillment === "delivery" ? p.delivery_address : null,
    feeCents: p.fulfillment === "delivery" ? Number(p.delivery_fee_cents || 0) : 0,
  });

/* ── Constructores de mensajes (shape del chat real + `sim` para el render rico) ── */

let _seq = 0;
function msg(fromMe, ts, messageBody, extra = {}) {
  _seq += 1;
  return {
    mensaje_id: `sim-food-${fromMe ? "out" : "in"}-${_seq}`,
    timestamp: ts,
    messageBody,
    messageType: "text",
    fromMe,
    direction: fromMe ? "outbound" : "inbound",
    status: "read",
    ...extra,
  };
}
const inText = (ts, text) => msg(false, ts, text);
const outText = (ts, text) => msg(true, ts, text);

/** Mensaje con el botón «Ver carta y pedir» que abre el Formulario (c_detectFoodOrderIntent). */
function flowInvite(ts) {
  const header = flowInviteHeader(FOOD_CHAT_BUSINESS);
  return msg(true, ts, `${header}\n${FLOW_INVITE_BODY}`, {
    messageType: "interactive",
    sim: { kind: "flow_invite", header, body: FLOW_INVITE_BODY, footer: FLOW_INVITE_FOOTER, cta: FLOW_INVITE_CTA },
  });
}

/** Lo que el cliente envía al terminar el Formulario (respuesta del Flow con su pedido). */
function flowReply(ts, o) {
  const summary = o.lines.map((l) => `${l.qty}× ${l.name}`).join(", ");
  return msg(false, ts, `${FLOW_INVITE_CTA}: ${summary}`, {
    messageType: "interactive",
    sim: {
      kind: "flow_reply",
      title: FLOW_INVITE_CTA,
      lines: o.lines.map((l) => ({ ...l, lineTotal: comma(l.price * l.qty) })),
      fulfillment: o.delivery ? "delivery" : "pickup",
      time: o.time,
      address: o.delivery ? o.delivery.address : null,
      shippingCents: o.delivery ? o.delivery.feeCents : null,
      total: comma(o.total),
    },
  });
}

/** Props del ticket PNG (WhatsappOrderTicket) para este pedido. */
function ticketProps(o) {
  const delivery = !!o.delivery;
  return {
    lines: o.lines.map((l) => ({ qty: l.qty, name: l.name, price: comma(l.price * l.qty) })),
    total: comma(o.total),
    pickup: o.time,
    stubTime: o.time,
    pickupLabel: delivery ? "Entrega a domicilio" : "Recogida",
    fulfillment: delivery ? "delivery" : "pickup",
    clientName: o.name,
    address: delivery ? o.delivery.address : null,
    shipping: delivery
      ? o.delivery.feeCents > 0
        ? { label: "Envío", value: `${comma(o.delivery.feeCents / 100)} €` }
        : { label: "Envío gratis", value: null }
      : null,
  };
}

/**
 * Confirmación con el ticket de cabecera.
 * - pay: plantilla de pago online (pedido_confirmado_pago_v4/_v5) + «Pagar online».
 * - flow: confirmación del Formulario (lleva «Cancelar pedido» y «Nuevo pedido»).
 * - Sin pay en un pedido del Formulario: texto de flowOrderConfirmation (🎉 ¡Pedido confirmado!).
 */
function confirmation(ts, o, { pay = false, flow = true } = {}) {
  const body = pay || !flow
    ? templateConfirmText({
        business: FOOD_CHAT_BUSINESS,
        lines: o.lines,
        total: o.total,
        pickupCaption: o.time,
        customerName: firstName(o.name),
        delivery: o.delivery ? { address: o.delivery.address } : null,
        pay,
      })
    : flowConfirmText({ lines: o.lines, pickupCaption: o.time, delivery: o.delivery });
  const buttons = [
    ...(pay ? [{ id: "pay", title: BTN_PAY_ONLINE, kind: "url" }] : []),
    { id: "cancel", title: BTN_CANCEL_ORDER },
    ...(flow ? [{ id: "new", title: BTN_NEW_ORDER }] : []),
  ];
  return msg(true, ts, body, {
    messageType: pay || !flow ? "template" : "interactive",
    sim: { kind: "confirmation", ticket: ticketProps(o), body, buttons, auto: true },
  });
}

/** El cliente pulsa un botón de respuesta de un mensaje anterior. */
const buttonTap = (ts, title) => msg(false, ts, title, { messageType: "button", sim: { kind: "button_reply", title } });

/** Aviso automático (plantilla o texto que manda el sistema, no una persona). */
const autoText = (ts, text) => msg(true, ts, text, { sim: { kind: "auto" } });

const readyNotice = (ts, name) => autoText(ts, fillTemplate(TPL_READY_CONEXT, [FOOD_CHAT_BUSINESS, firstName(name)]));
const paidNotice = (ts, o) =>
  autoText(ts, paidNoticeText({ name: firstName(o.name), amountEuros: o.total, pickupTime: o.time, delivery: !!o.delivery }));

/* ── Las conversaciones ─────────────────────────────────────────────────────── */

function buildConversations() {
  _seq = 0;
  const chats = [];
  const add = (p, messages, { unread = 0 } = {}) => chats.push({ name: p.contact_name || p.name, phone: p.phone, messages, unread });

  // 1. Carlos Ruiz — pide por la WEB (enlace /carta), recogida 13:30, aún sin pagar, primer pedido.
  {
    const p = order("ord-001");
    const o = fromSeed(p);
    add(p, [
      confirmation(p.created_at, o, { pay: true, flow: false }),
      inText(at(p.created_at, 2), "Hola, ¿puedo pagar al recogerlo?"),
      inText(at(p.created_at, 3), "Lo de sin cebolla lo he puesto en la nota 🙏"),
    ], { unread: 2 });
  }

  // 2. María López — escribe, Formulario, recogida 13:00, PAGA ONLINE → aviso de pago recibido.
  {
    const p = order("ord-002");
    const o = fromSeed(p);
    add(p, [
      inText(at(p.created_at, -3), "Hola, quiero hacer un pedido"),
      flowInvite(at(p.created_at, -3)),
      flowReply(p.created_at, o),
      confirmation(p.created_at, o, { pay: true }),
      paidNotice(p.paid_at, o),
      inText(at(p.paid_at, 1), "¡Genial, gracias! 😊"),
    ]);
  }

  // 3. Ana García — pulsa el enlace de pedidos, Formulario del menú, recogida 12:30, paga en el
  //    local → «listo para recoger» → contesta.
  {
    const p = order("ord-003");
    const o = fromSeed(p);
    const readyAt = p.status_history.find((h) => h.status === "Listo").at;
    add(p, [
      inText(at(p.created_at, -2), `Hola, quiero hacer un pedido en ${FOOD_CHAT_BUSINESS}`),
      flowInvite(at(p.created_at, -2)),
      flowReply(p.created_at, o),
      confirmation(p.created_at, o),
      readyNotice(readyAt, p.contact_name),
      inText(at(readyAt, 2), "Voy para allá, ¡gracias!"),
    ]);
  }

  // 4. Pedro Martínez — lo pide escribiendo; el negocio lo apunta a mano (14:00) → confirmación
  //    con ticket → «listo para recoger» → gracias.
  {
    const p = order("ord-004");
    const o = fromSeed(p);
    const readyAt = p.status_history.find((h) => h.status === "Listo").at;
    add(p, [
      inText(at(p.created_at, -4), "Buenas, ¿me guardáis una merluza al horno y un flan para las 14:00? Paso yo."),
      outText(at(p.created_at, -1), "¡Hecho, Pedro! Ahora te llega la confirmación."),
      confirmation(p.created_at, o, { flow: false }),
      readyNotice(readyAt, p.contact_name),
      inText(at(readyAt, 3), "Muchas gracias 👍"),
    ]);
  }

  // 5. Isabel Sánchez — hace 2 días, Formulario A DOMICILIO (envío gratis desde 20 €), paga al
  //    repartidor. Hoy ha comprado en el mostrador (TPV): eso no pasa por el chat.
  {
    const p = order("ord-005");
    const o = chatOrder({
      name: p.contact_name,
      items: [{ qty: 3, name: "Croquetas de jamón ibérico" }, { qty: 1, name: "Coca-Cola 33cl" }],
      time: "21:00",
      address: "Av. del Puerto 82, 5º A",
      feeCents: 0,
    });
    const t0 = daysAgoAt(2, 20, 12);
    add(p, [
      inText(t0, "Hola! ¿Hacéis envío a domicilio?"),
      flowInvite(at(t0, 1)),
      flowReply(at(t0, 4), o),
      confirmation(at(t0, 4), o),
      outText(daysAgoAt(2, 20, 52), "¡Hola Isabel! Tu pedido ya va de camino 🛵"),
      inText(daysAgoAt(2, 21, 6), "Recibido, ¡estaban buenísimas las croquetas!"),
    ]);
  }

  // 6. Roberto Fernández — su pedido de la semana pasada, y hoy REPITE con «Nuevo pedido».
  {
    const p = order("ord-006");
    const o = fromSeed(p);
    const t0 = daysAgoAt(8, 12, 40);
    add(p, [
      inText(t0, "Hola, quiero hacer un pedido"),
      flowInvite(at(t0, 0)),
      flowReply(at(t0, 3), o),
      confirmation(at(t0, 3), o),
      readyNotice(daysAgoAt(8, 14, 30), p.contact_name),
      inText(daysAgoAt(8, 14, 34), "Gracias!"),
      buttonTap(at(p.created_at, -3), BTN_NEW_ORDER),
      flowInvite(at(p.created_at, -3)),
      flowReply(p.created_at, o),
      confirmation(p.created_at, o),
    ]);
  }

  // 7. Laura Jiménez — Formulario A DOMICILIO, paga online → pago recibido → «va de camino».
  {
    const p = order("ord-009");
    const o = fromSeed(p);
    const readyAt = p.status_history.find((h) => h.status === "Listo").at;
    add(p, [
      inText(at(p.created_at, -2), "Hola! Quiero pedir para comer en casa"),
      flowInvite(at(p.created_at, -2)),
      flowReply(p.created_at, o),
      confirmation(p.created_at, o, { pay: true }),
      paidNotice(p.paid_at, o),
      outText(at(readyAt, 1), "¡Hola Laura! Tu pedido ya va de camino 🛵"),
      inText(at(readyAt, 2), "¡Perfecto, gracias!"),
    ]);
  }

  // 8. Diego Torres — Formulario, recogida 14:30, y CANCELA con el botón de la confirmación.
  {
    const p = order("ord-010");
    const o = fromSeed(p);
    add(p, [
      inText(at(p.created_at, -2), "Buenas, ¿tenéis paella hoy?"),
      flowInvite(at(p.created_at, -2)),
      flowReply(p.created_at, o),
      confirmation(p.created_at, o),
      buttonTap(p.cancelled_at, BTN_CANCEL_ORDER),
      autoText(p.cancelled_at, CANCEL_REPLIES.cancelled),
      inText(at(p.cancelled_at, 1), "Perdón, al final comemos fuera. ¡Otro día!"),
    ]);
  }

  return chats;
}

const contactIdOf = (companyId, phone) => `${companyId}#${phone}`;

/** Texto corto del último mensaje para la lista de chats. */
function snippetOf(m) {
  if (!m) return "";
  if (m.sim?.kind === "confirmation") return m.sim.body.split("\n")[0].replace(/\*/g, "");
  return String(m.messageBody || "").replace(/\*/g, "").replace(/\s+/g, " ").trim();
}

/** Lista de chats (misma forma que /chat-list/unified), del más reciente al más antiguo. */
export function buildFoodChatList(companyId) {
  return buildConversations()
    .map((c) => {
      const last = c.messages[c.messages.length - 1];
      return {
        company_id: companyId,
        contact_id: contactIdOf(companyId, c.phone),
        nombre: c.name,
        phone_number: c.phone,
        snippet: snippetOf(last),
        snippet_status: last.fromMe ? "read" : undefined,
        last_msg_ts: last.timestamp,
        channel: "whatsapp",
        unread_count: c.unread,
      };
    })
    .sort((a, b) => b.last_msg_ts.localeCompare(a.last_msg_ts));
}

/** Mensajes de un chat (misma forma que /chat/{id}/messages). null si no es de la demo. */
export function buildFoodChatMessages(companyId, contactId) {
  const id = decodeURIComponent(String(contactId || ""));
  const chat = buildConversations().find((c) => contactIdOf(companyId, c.phone) === id);
  if (!chat) return null;
  const items = [...chat.messages].sort((a, b) => a.timestamp.localeCompare(b.timestamp));
  return { items, lastKey: null };
}
